/**
 * InvoicesList Component
 * 
 * Displays a list of all invoices with their key details.
 * Features:
 *   - Pagination to handle large numbers of invoices
 *   - Filtering by customer, date range, and status
 *   - Status indicators for paid/finalized state
 *   - Action buttons for view, edit, delete, and finalize
 *   - Polish accounting software style design
 *   - Deep links - URL stores filters, sorting, and pagination
 *   - Clickable rows to view invoice details
 */
import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSortUp,
  faSortDown,
  faSort,
} from '@fortawesome/free-solid-svg-icons';

import { useApi } from 'api';
import { Invoice } from 'types';
import { ConfirmModal, LoadingSpinner } from '../common';
import InvoicesFilters from './InvoicesFilters';
import InvoiceRow from './InvoiceRow';
import Pagination from './Pagination';
import { 
  buildSortParam, 
  parseInvoiceTotal,
  parseInvoiceTax,
  SortColumn,
} from '../../utils/invoiceUtils';
import { useInvoiceActions, useInvoiceNavigation } from 'app/hooks';
import { useInvoiceListUrlState } from './useInvoiceListUrlState';
import {
  buildFilterParam,
  hasActiveFilters as checkActiveFilters,
  countActiveFilters,
} from './filterUtils';
import { logger } from '../../utils/logger';

const InvoicesList: React.FC = () => {
  const api = useApi();
  
  // URL state management (pagination, sorting, filters)
  const urlState = useInvoiceListUrlState();

  // State for invoices data
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Request ID ref to prevent race conditions - only the latest request updates state
  const latestRequestIdRef = useRef(0);

  // Destructure stable values from urlState for dependencies
  const { 
    currentPage, 
    pageSize, 
    sortColumn, 
    sortDirection, 
    appliedFilters,
    setTotals 
  } = urlState;

  /**
   * Fetch invoices from the API with server-side sorting and filtering
   * 
   * Uses request ID guard to prevent race conditions:
   * - Each fetch increments the request ID
   * - Only the response from the latest request updates state
   * - Stale responses from earlier requests are discarded
   */
  const fetchInvoices = useCallback(async () => {
    // Generate a new request ID for this fetch
    const requestId = ++latestRequestIdRef.current;
    
    setIsLoading(true);
    setError(null);

    try {
      const sort = buildSortParam(sortColumn, sortDirection);
      const filter = buildFilterParam(appliedFilters);
      
      const { data } = await api.getInvoices({
        page: currentPage,
        per_page: pageSize,
        sort,
        filter,
      });

      // Only update state if this is still the latest request
      // Prevents stale responses from overwriting newer data
      if (requestId !== latestRequestIdRef.current) {
        return; // Discard stale response
      }

      setInvoices(data.invoices);
      setTotals(data.pagination.total_pages, data.pagination.total_entries);
    } catch (err) {
      // Only show error if this is still the latest request
      if (requestId !== latestRequestIdRef.current) {
        return;
      }
      setError('Failed to load invoices. Please try again.');
      logger.error('Error fetching invoices:', err);
    } finally {
      // Only update loading state if this is still the latest request
      if (requestId === latestRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [api, currentPage, pageSize, sortColumn, sortDirection, appliedFilters, setTotals]);

  // Load invoices when dependencies change
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Invoice actions (delete, finalize, toggle paid) - DRY hook
  const { deleteAction, finalizeAction, togglePaid, togglingPaidId } = useInvoiceActions({
    onDeleteSuccess: fetchInvoices,
    onFinalizeSuccess: fetchInvoices,
    onTogglePaidSuccess: fetchInvoices,
  });

  // Navigation helpers - DRY hook for URL building with list state preservation
  const navigation = useInvoiceNavigation({
    getCurrentSearchParams: urlState.getCurrentSearchParams,
  });

  // Check if any filters are active
  const hasActiveFilters = useMemo(
    () => checkActiveFilters(urlState.appliedFilters),
    [urlState.appliedFilters]
  );

  // Count active filters
  const activeFilterCount = useMemo(
    () => countActiveFilters(urlState.appliedFilters),
    [urlState.appliedFilters]
  );

  /**
   * Get sort icon for a column
   */
  const getSortIcon = (column: SortColumn) => {
    if (urlState.sortColumn !== column) {
      return faSort;
    }
    return urlState.sortDirection === 'asc' ? faSortUp : faSortDown;
  };

  /**
   * Handle page change with validation
   */
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= urlState.totalPages) {
      urlState.setCurrentPage(newPage);
    }
  };

  /**
   * Handle row click to view invoice
   */
  const handleRowClick = (id: number, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }
    navigation.navigateToDetail(id);
  };

  /**
   * Toggle paid status for an invoice
   */
  const handleTogglePaid = (invoice: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();
    togglePaid(invoice.id, invoice.paid);
  };

  /**
   * Calculate totals and taxes from invoice lines
   */
  const invoiceCalculations = useMemo(() => {
    const calculations: Record<number, { total: number; tax: number }> = {};
    
    for (const invoice of invoices) {
      calculations[invoice.id] = {
        total: parseInvoiceTotal(invoice.total, invoice.invoice_lines),
        tax: parseInvoiceTax(invoice.tax, invoice.invoice_lines),
      };
    }
    
    return calculations;
  }, [invoices]);

  // Show loading spinner while fetching
  if (isLoading && invoices.length === 0) {
    return <LoadingSpinner message="Loading invoices..." />;
  }

  // Show error message if fetch failed
  if (error) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="empty-state">
            <p className="empty-state-title">{error}</p>
            <button className="btn btn-primary mt-3" onClick={fetchInvoices}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Invoices</h1>
        <button className="btn-add-invoice" onClick={navigation.navigateToNew} title="New Invoice">
          <FontAwesomeIcon icon={faPlus} />
          <span>New Invoice</span>
        </button>
      </div>

      {/* Invoice Table Card */}
      <div className="card">
        {/* Filters Component */}
        <InvoicesFilters
          filters={urlState.filters}
          appliedFilters={urlState.appliedFilters}
          isExpanded={urlState.isFilterExpanded}
          hasActiveFilters={hasActiveFilters}
          activeFilterCount={activeFilterCount}
          onFiltersChange={urlState.setFilters}
          onApply={urlState.applyFilters}
          onClear={urlState.clearFilters}
          onRemoveFilter={urlState.removeFilter}
          onToggleExpanded={() => urlState.setIsFilterExpanded(!urlState.isFilterExpanded)}
        />

        {invoices.length === 0 && !isLoading ? (
          <div className="empty-state">
            <p className="empty-state-title">No invoices yet</p>
            <p>Create your first invoice to get started.</p>
            <button className="btn btn-primary mt-3" onClick={navigation.navigateToNew}>
              <FontAwesomeIcon icon={faPlus} />
              Create Invoice
            </button>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th className="cell-number sortable" onClick={() => urlState.handleSort('id')}>
                    Number
                    <FontAwesomeIcon icon={getSortIcon('id')} className="sort-icon" />
                  </th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th className="text-center sortable" onClick={() => urlState.handleSort('date')}>
                    Date
                    <FontAwesomeIcon icon={getSortIcon('date')} className="sort-icon" />
                  </th>
                  <th className="text-center sortable" onClick={() => urlState.handleSort('deadline')}>
                    Deadline
                    <FontAwesomeIcon icon={getSortIcon('deadline')} className="sort-icon" />
                  </th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <InvoiceRow
                    key={invoice.id} 
                    invoice={invoice}
                    calculations={invoiceCalculations[invoice.id] || { total: 0, tax: 0 }}
                    isTogglingPaid={togglingPaidId === invoice.id}
                    onRowClick={handleRowClick}
                    onViewClick={navigation.navigateToDetail}
                    onEditClick={navigation.navigateToEdit}
                    onCloneClick={navigation.navigateToClone}
                    onTogglePaid={handleTogglePaid}
                    onFinalizeClick={finalizeAction.open}
                    onDeleteClick={deleteAction.open}
                  />
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <Pagination
              currentPage={urlState.currentPage}
              totalPages={urlState.totalPages}
              totalCount={urlState.totalCount}
              pageSize={urlState.pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={urlState.setPageSize}
            />
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteAction.isOpen}
        title="Delete Invoice"
        message="Are you sure you want to delete this invoice? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={deleteAction.confirm}
        onCancel={deleteAction.cancel}
        isLoading={deleteAction.isLoading}
      />

      {/* Finalize Confirmation Modal */}
      <ConfirmModal
        isOpen={finalizeAction.isOpen}
        title="Finalize Invoice"
        message="Once finalized, this invoice cannot be edited anymore. Only the payment status can be changed. Are you sure?"
        confirmLabel="Finalize"
        confirmVariant="primary"
        onConfirm={finalizeAction.confirm}
        onCancel={finalizeAction.cancel}
        isLoading={finalizeAction.isLoading}
      />
    </>
  );
};

export default InvoicesList;
